import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { TpvDashboardRepository } from '@app/domain/ports/tpv/tpv-dashboard.repository';
import {
  TpvDashboardQuery,
  TpvDashboardSummary,
} from '@app/domain/models/tpv/tpv-dashboard-summary.model';

/**
 * Mock del Dashboard de TPV para pruebas sin backend (`tpvUseMocks`).
 *
 * Reproduce los valores del contrato (DASHBOARD_TPV_CONTRATO_FRONT.md). Si se
 * pasa `idMerchant`, devuelve un dataset reducido para simular el drill-down a
 * un solo comercio (caso operador acotado del contrato).
 */
@Injectable()
export class TpvDashboardMockDataSource extends TpvDashboardRepository {
  override getDashboard(query: TpvDashboardQuery = {}): Observable<TpvDashboardSummary> {
    const summary = query.idMerchant ? this.singleMerchant() : this.global();
    return of(summary).pipe(delay(300));
  }

  /** Vista global (todos los comercios del scope) — ejemplo del contrato. */
  private global(): TpvDashboardSummary {
    return {
      totalSales: { count: 372, amount: 52001.9 },
      cancellations: { count: 24, amount: 1809.5 },
      netSales: { count: 348, amount: 50192.4 },
      enabledMerchants: 115,
      activeMerchants: 11,
      avgTransactionsPerMerchant: 37,
      averageAmount: 139.79,
      transactionsTotal: 407,
      paymentByMethod: {
        slices: [
          { label: 'Crédito', value: 339, percentage: 83.29 },
          { label: 'Débito', value: 66, percentage: 16.22 },
          { label: 'Meses', value: 2, percentage: 0.49 },
        ],
      },
      paymentByBrand: {
        slices: [
          { label: 'VISA', value: 320, percentage: 78.62 },
          { label: 'MASTERCARD', value: 77, percentage: 18.92 },
          { label: 'AMEX', value: 10, percentage: 2.46 },
        ],
      },
      monthlyAmount: [
        { month: '2026-02', value: 23640.5 },
        { month: '2026-03', value: 3459.3 },
        { month: '2026-04', value: 23885 },
        { month: '2026-05', value: 992.1 },
        { month: '2026-06', value: 25 },
      ],
      monthlyTransactions: [
        { month: '2026-02', value: 29 },
        { month: '2026-03', value: 230 },
        { month: '2026-04', value: 51 },
        { month: '2026-05', value: 72 },
        { month: '2026-06', value: 25 },
      ],
    };
  }

  /** Drill-down a un comercio (operador acotado del contrato: 5 ventas / $800.50). */
  private singleMerchant(): TpvDashboardSummary {
    return {
      totalSales: { count: 5, amount: 800.5 },
      cancellations: { count: 0, amount: 0 },
      netSales: { count: 5, amount: 800.5 },
      enabledMerchants: 1,
      activeMerchants: 1,
      avgTransactionsPerMerchant: 5,
      averageAmount: 160.1,
      transactionsTotal: 5,
      paymentByMethod: {
        slices: [
          { label: 'Crédito', value: 4, percentage: 80 },
          { label: 'Débito', value: 1, percentage: 20 },
        ],
      },
      paymentByBrand: {
        slices: [
          { label: 'VISA', value: 3, percentage: 60 },
          { label: 'MASTERCARD', value: 2, percentage: 40 },
        ],
      },
      monthlyAmount: [
        { month: '2026-05', value: 480.3 },
        { month: '2026-06', value: 320.2 },
      ],
      monthlyTransactions: [
        { month: '2026-05', value: 3 },
        { month: '2026-06', value: 2 },
      ],
    };
  }
}
